import {
  createNotice,
  deleteNotice,
  editNotice,
  recordNoticeDelivery,
  sendNotice,
  useListNotices,
  type DeliveryMethod,
  type NoticeKind,
  type NoticeOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { optional, required, type Field } from '../../components/FormFields';
import { TablePager, useTablePage } from '../../components/TablePager';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, formatDateTime, todayIso } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { DELIVERY, NEW_NOTICE_KINDS, NOTICE_KIND } from './labels';
import { useLeaseOptions } from './useLeaseOptions';

const KIND_OPTIONS = NEW_NOTICE_KINDS.map((value) => ({ value, label: NOTICE_KIND[value] }));
const DELIVERY_FIELDS: Field[] = [
  {
    name: 'delivery_method',
    label: 'How It Was Delivered',
    type: 'select',
    required: true,
    options: (Object.keys(DELIVERY) as DeliveryMethod[]).map((value) => ({
      value,
      label: DELIVERY[value],
    })),
  },
  { name: 'delivered_on', label: 'Date Delivered', type: 'date', required: true },
];
const EDIT_FIELDS: Field[] = [
  { name: 'title', label: 'Title', required: true },
  { name: 'body', label: 'Notice', type: 'multiline', required: true },
  { name: 'respond_by', label: 'They Have Until', type: 'date' },
];

type Dialog = { kind: 'add' } | { kind: 'edit' | 'delivery'; notice: NoticeOut } | null;

export function NoticesPage() {
  const canManage = useCan('notices.manage');
  const queryClient = useQueryClient();
  const paging = useTablePage();
  const [q, setQ] = useState('');
  const query = useListNotices({ q, ...paging.params });
  const notices = okItems(query.data);
  const leases = useLeaseOptions();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [error, setError] = useState<string | null>(null);

  async function act(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  const addFields: Field[] = [
    { name: 'lease_id', label: 'Lease', type: 'select', required: true, options: leases },
    { name: 'kind', label: 'Type', type: 'select', required: true, options: KIND_OPTIONS },
    {
      name: 'respond_by',
      label: 'They Have Until',
      type: 'date',
      helperText: 'Needed for Pay or Quit and Lease Violation',
    },
  ];

  const columns: DataColumn<NoticeOut>[] = [
    { label: 'Notice', render: (n) => n.title },
    { label: 'Type', render: (n) => NOTICE_KIND[n.kind] },
    { label: 'Residents', render: (n) => n.residents || '–' },
    { label: 'Sent', render: (n) => (n.sent_at ? formatDateTime(n.sent_at) : '–') },
    { label: 'They Have Until', render: (n) => (n.respond_by ? formatDate(n.respond_by) : '–') },
    {
      label: 'Delivered',
      render: (n) =>
        n.delivery_method
          ? `${DELIVERY[n.delivery_method]}, ${formatDate(n.delivered_on)}`
          : n.status === 'sent'
            ? 'Email and Portal'
            : '–',
    },
    {
      label: 'Status',
      render: (n) =>
        n.status === 'sent' ? (
          <Chip size="small" color="success" label="Sent" />
        ) : (
          <Chip size="small" color="warning" label="Draft" />
        ),
    },
    {
      label: 'Actions',
      align: 'right',
      render: (n) => (
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'flex-end', whiteSpace: 'nowrap' }}
        >
          <Button size="small" href={`/api/notices/${n.id}/pdf`}>
            PDF
          </Button>
          {canManage && n.status === 'draft' && (
            <>
              <Button size="small" onClick={() => setDialog({ kind: 'edit', notice: n })}>
                Edit
              </Button>
              <Button
                size="small"
                onClick={() => {
                  if (window.confirm('Send this notice? It can’t be changed afterward.')) {
                    void act(() => sendNotice(n.id));
                  }
                }}
              >
                Send
              </Button>
              <Button size="small" color="error" onClick={() => void act(() => deleteNotice(n.id))}>
                Remove
              </Button>
            </>
          )}
          {canManage && n.status === 'sent' && (
            <Button size="small" onClick={() => setDialog({ kind: 'delivery', notice: n })}>
              Record Delivery
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h5">Notices</Typography>
          <Typography color="text.secondary">
            Formal letters to residents. Sending emails them and adds it to their portal.
          </Typography>
        </div>
        <TextField
          size="small"
          label="Search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            paging.setPage(0);
          }}
        />
        {canManage && (
          <Button variant="contained" onClick={() => setDialog({ kind: 'add' })}>
            New Notice
          </Button>
        )}
      </Stack>
      <Alert severity="warning">
        These are starting points, not legal forms. Rules on wording, notice periods and delivery
        vary by state, and email alone may not count as legal delivery. Have your attorney check
        anything that could lead to an eviction.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      {notices.length === 0 ? (
        <Typography color="text.secondary">No notices yet.</Typography>
      ) : (
        <>
          <DataTable columns={columns} rows={notices} rowKey={(n) => n.id} />
          <TablePager paging={paging} total={okData(query.data)?.total ?? 0} />
        </>
      )}
      {dialog?.kind === 'add' && (
        <FormDialog
          title="New Notice"
          intro="This starts a draft you can read and edit. Nothing goes out until you send it."
          fields={addFields}
          initial={{ lease_id: '', kind: 'late', respond_by: '' }}
          submitLabel="Start Draft"
          onSubmit={(values) =>
            createNotice({
              lease_id: Number(required(values, 'lease_id')),
              kind: required(values, 'kind') as NoticeKind,
              respond_by: optional(values, 'respond_by'),
            })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'edit' && (
        <FormDialog
          title="Edit Notice"
          fields={EDIT_FIELDS}
          initial={{
            title: dialog.notice.title,
            body: dialog.notice.body,
            respond_by: dialog.notice.respond_by ?? '',
          }}
          onSubmit={(values) =>
            editNotice(dialog.notice.id, {
              title: required(values, 'title'),
              body: required(values, 'body'),
              amount: dialog.notice.amount,
              respond_by: optional(values, 'respond_by'),
            })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'delivery' && (
        <FormDialog
          title="Record Delivery"
          intro="For delivery outside this app, such as by hand or certified mail."
          fields={DELIVERY_FIELDS}
          initial={{
            delivery_method: dialog.notice.delivery_method ?? 'hand',
            delivered_on: dialog.notice.delivered_on ?? todayIso(),
          }}
          onSubmit={(values) =>
            recordNoticeDelivery(dialog.notice.id, {
              delivery_method: required(values, 'delivery_method') as DeliveryMethod,
              delivered_on: required(values, 'delivered_on'),
            })
          }
          onClose={() => setDialog(null)}
        />
      )}
    </Stack>
  );
}
